import type { CssKeyframe } from './pose';
import { readable, shade, withAlpha } from './palette';
import type { PresetParam } from './presets';
import type { FxAnim, FxContext, FxKind, FxNode } from './fx';
import {
  BACK,
  OUT,
  P_COLOR,
  P_COLOR2,
  P_COLOR3,
  P_COUNT,
  P_OPACITY,
  P_TARGET,
  P_TEXT,
  P_X,
  P_Y,
  color,
  fitFont,
  full,
  headlineWords,
  lbl,
  num,
  px,
  r2,
  rng,
  str,
  unit,
} from './fx-kit';
import { FX_MONO_FONT } from './fx-fonts';

/**
 * Motion design graphics in the style of launch films and motion reels: a viewfinder HUD, a
 * terminal typing a prompt, a code editor, kinetic words, sliced titles, dot grids, particle
 * orbits, rings of text, a striped ribbon drawn along a curve, counters and bar charts.
 *
 * Like every fx kind they are pure data (nodes with Web Animations keyframes) built from the
 * parameters and the template's palette, deterministic for a given item id.
 */

// ---------------------------------------------------------------- shared

export const MONO = FX_MONO_FONT;
export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
export const clamp01 = (v: number) => clamp(v, 0, 1);
export const deg = (v: number) => `${r2(v)}deg`;

/** Lines of a text parameter: one per line, or separated by " / ". */
export function linesOf(ctx: FxContext, key: string, fallback: string, max = 12): string[] {
  return str(ctx, key, fallback)
    .split(/\n|\s\/\s/)
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .slice(0, max);
}

export const glyphs = (s: string) => [...s].length;

/** Shown from `from` to `to` (ms of the item), hidden before and after: hard cuts. */
export function windowAnim(D: number, from: number, to: number): FxAnim {
  const s = clamp01(from / D);
  const e = Math.max(s, clamp01(to / D));
  return {
    delay: 0,
    duration: D,
    fill: 'both',
    easing: 'linear',
    keyframes: [
      { opacity: 0, offset: 0 },
      { opacity: 0, offset: s },
      { opacity: 1, offset: s },
      { opacity: 1, offset: e },
      { opacity: e >= 1 ? 1 : 0, offset: e },
      { opacity: e >= 1 ? 1 : 0, offset: 1 },
    ],
  };
}

/** Appears at `at` and stays. */
export const showAt = (at: number): FxAnim => ({
  delay: Math.max(0, at),
  duration: 1,
  fill: 'both',
  keyframes: [{ opacity: 0 }, { opacity: 1 }],
});

/** On/off blinking (a caret, a REC light) from `from` to the end of the item. */
export function blink(D: number, from = 0, period = 1000): FxAnim {
  from = clamp(from, 0, Math.max(0, D - 16));
  period = Math.max(16, Math.min(period, D - from));
  return {
    delay: from,
    duration: period,
    iterations: Math.max(1, Math.ceil((D - from) / period)),
    fill: 'both',
    easing: 'linear',
    keyframes: [
      { opacity: 1, offset: 0 },
      { opacity: 1, offset: 0.55 },
      { opacity: 0, offset: 0.55 },
      { opacity: 0, offset: 1 },
    ],
  };
}

/** Typing: a monospaced span grows one character at a time. */
export function typeAnim(chars: number, start: number, perChar: number): FxAnim {
  return {
    delay: Math.max(0, start),
    duration: Math.max(16, chars * perChar),
    fill: 'both',
    easing: `steps(${Math.max(1, chars)}, end)`,
    keyframes: [{ width: '0ch' }, { width: `${chars}ch` }],
  };
}

/**
 * Typing schedule of several lines: when each starts and how long a character takes. Pauses and
 * speed shrink together when the item is too short, so typing always ends by 85 % of it.
 */
export function typingPlan(
  counts: number[],
  D: number,
  lead: number,
  pause: number,
  perChar: number,
) {
  const total = counts.reduce((s, n) => s + n, 0);
  const need = lead + pause * (counts.length - 1) + total * perChar;
  const k = Math.min(1, (D * 0.85) / Math.max(1, need));
  const starts: number[] = [];
  let at = lead * k;
  for (const n of counts) {
    starts.push(at);
    at += n * perChar * k + pause * k;
  }
  return { starts, perChar: Math.max(0.5, perChar * k) };
}

/** Arrival used by panels and labels: rises a little and fades in. */
export const arrive = (delay: number, duration = 500, dy = 12): FxAnim => ({
  delay,
  duration,
  fill: 'both',
  easing: OUT,
  keyframes: [
    { opacity: 0, transform: `translateY(${px(dy)})` },
    { opacity: 1, transform: 'translateY(0px)' },
  ],
});

/**
 * A digit (or any glyph) wheel: a one-glyph window over a column of glyphs. With `cycle` the
 * column rolls through all glyphs every `cycle` ms (timecodes); with `frames` it jumps to the
 * glyph `i` at offset `at` (counters).
 */
export function reel(
  list: string[],
  h: number,
  D: number,
  opts: { cycle?: number; frames?: Array<{ at: number; i: number }>; offset?: number },
): FxNode {
  const anims: FxAnim[] = [];
  const offset = Math.max(0, opts.offset ?? 0);
  if (opts.cycle && (opts.cycle >= D || offset)) {
    // Slow wheels (and wheels that start mid-count): the glyphs they reach, as exact steps
    const per = opts.cycle / list.length;
    const first = Math.floor(offset / per);
    const frames = [{ at: 0, i: first % list.length }];
    for (let j = 1; (first + j) * per - offset < D; j++)
      frames.push({ at: ((first + j) * per - offset) / D, i: (first + j) % list.length });
    return reel(list, h, D, { frames });
  }
  if (opts.cycle) {
    anims.push({
      delay: 0,
      duration: opts.cycle,
      iterations: Math.max(1, Math.ceil(D / opts.cycle)),
      fill: 'both',
      easing: `steps(${list.length}, end)`,
      keyframes: [
        { transform: 'translateY(0px)' },
        { transform: `translateY(${px(-list.length * h)})` },
      ],
    });
  } else if (opts.frames?.length) {
    const keyframes: CssKeyframe[] = opts.frames.map((f) => ({
      transform: `translateY(${px(-f.i * h)})`,
      offset: clamp01(f.at),
      easing: 'steps(1, end)',
    }));
    if (keyframes[0].offset !== 0) keyframes.unshift({ ...keyframes[0], offset: 0 });
    const last = keyframes[keyframes.length - 1];
    if (last.offset !== 1) keyframes.push({ ...last, offset: 1 });
    anims.push({ delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes });
  }
  return {
    style: {
      display: 'inline-block',
      position: 'relative',
      width: '1ch',
      height: px(h),
      overflow: 'hidden',
      verticalAlign: 'top',
    },
    children: [
      {
        style: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
        children: list.map((g) => ({
          text: g,
          style: { height: px(h), lineHeight: px(h), whiteSpace: 'pre' },
        })),
        anims,
      },
    ],
  };
}

export const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Digit wheels counting from `from` to `to` in `run` ms (fast at first, settling at the end);
 * leading digits stay blank until the number reaches them.
 */
export function countUp(from: number, to: number, run: number, D: number, h: number): FxNode[] {
  const nd = Math.max(String(from).length, String(to).length);
  const S = clamp(Math.round(run / 45), 10, 90);
  const ease = (x: number) => 1 - (1 - x) ** 3;
  const values = Array.from({ length: S + 1 }, (_, i) =>
    Math.round(from + (to - from) * ease(i / S)),
  );
  const digitAt = (v: number, k: number) => {
    const p = 10 ** (nd - 1 - k);
    return v >= p || k === nd - 1 ? Math.floor(v / p) % 10 : 10;
  };
  const k = Math.min(1, run / D);
  return Array.from({ length: nd }, (_, d) => {
    const frames: Array<{ at: number; i: number }> = [];
    values.forEach((v, i) => {
      const g = digitAt(v, d);
      if (!frames.length || frames[frames.length - 1].i !== g)
        frames.push({ at: (i / S) * k, i: g });
    });
    return reel([...DIGITS, ' '], h, D, { frames });
  });
}

export const P_SIZE = (d: number, min: number, max: number): PresetParam => ({
  key: 'size',
  type: 'number',
  default: d,
  min,
  max,
  step: 0.5,
  label: lbl('Size, %', 'Розмір, %', 'Rozmiar, %', 'Mărime, %', 'Tamaño, %', 'Размер, %'),
});
export const P_LONGTEXT = (d: string): PresetParam => ({
  key: 'text',
  type: 'longtext',
  default: d,
  label: lbl(
    'Text (one item per line)',
    'Текст (по рядку на елемент)',
    'Tekst (jeden element w linii)',
    'Text (câte un element pe rând)',
    'Texto (un elemento por línea)',
    'Текст (по строке на элемент)',
  ),
});
export const P_SPEED = (d: number): PresetParam => ({
  key: 'speed',
  type: 'number',
  default: d,
  min: 2,
  max: 80,
  label: lbl(
    'Typing, chars/s',
    'Друк, симв./с',
    'Pisanie, znaki/s',
    'Tastare, car./s',
    'Escritura, car./s',
    'Печать, симв./с',
  ),
});
export const P_TURNS = (d: number): PresetParam => ({
  key: 'turns',
  type: 'number',
  default: d,
  min: -5,
  max: 5,
  step: 0.05,
  label: lbl('Turns', 'Оберти', 'Obroty', 'Rotații', 'Vueltas', 'Обороты'),
});
export const onOff = (key: string, d: 'on' | 'off', l: ReturnType<typeof lbl>): PresetParam => ({
  key,
  type: 'select',
  default: d,
  label: l,
  options: [
    { value: 'on', label: lbl('On', 'Увімк.', 'Wł.', 'Pornit', 'Sí', 'Вкл.') },
    { value: 'off', label: lbl('Off', 'Вимк.', 'Wył.', 'Oprit', 'No', 'Выкл.') },
  ],
});
export const opt = (value: string, l: ReturnType<typeof lbl>) => ({ value, label: l });

export const monoText = (
  ctx: FxContext,
  fs: number,
  extra: Record<string, string | number> = {},
) => ({
  fontFamily: MONO,
  fontWeight: 500,
  fontSize: px(fs),
  lineHeight: '1',
  whiteSpace: 'pre',
  fontVariantNumeric: 'tabular-nums',
  fontVariantLigatures: 'none',
  ...extra,
});

// ---------------------------------------------------------------- interface

const hud: FxKind = {
  id: 'hud',
  label: lbl(
    'Viewfinder HUD',
    'HUD відошукача',
    'HUD wizjera',
    'HUD de vizor',
    'HUD de visor',
    'HUD видоискателя',
  ),
  category: 'interface',
  z: 'front',
  duration: 8000,
  params: [
    {
      key: 'label',
      type: 'text',
      default: 'MOTION REEL',
      label: lbl(
        'Top left',
        'Зліва вгорі',
        'Lewy górny',
        'Stânga sus',
        'Arriba a la izquierda',
        'Слева вверху',
      ),
    },
    {
      key: 'label2',
      type: 'text',
      default: 'REC',
      label: lbl(
        'Top right',
        'Справа вгорі',
        'Prawy górny',
        'Dreapta sus',
        'Arriba a la derecha',
        'Справа вверху',
      ),
    },
    {
      key: 'label3',
      type: 'text',
      default: '',
      label: lbl(
        'Bottom left',
        'Зліва внизу',
        'Lewy dolny',
        'Stânga jos',
        'Abajo a la izquierda',
        'Слева внизу',
      ),
    },
    onOff(
      'timecode',
      'on',
      lbl('Timecode', 'Таймкод', 'Kod czasowy', 'Timecode', 'Código de tiempo', 'Таймкод'),
    ),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
    {
      key: 'inset',
      type: 'number',
      default: 4,
      min: 1,
      max: 15,
      step: 0.5,
      label: lbl('Margin, %', 'Відступ, %', 'Margines, %', 'Margine, %', 'Margen, %', 'Отступ, %'),
    },
    P_OPACITY(0.85),
    {
      key: 'from',
      type: 'number',
      default: 0,
      min: 0,
      max: 600_000,
      label: lbl(
        'Timecode from, ms',
        'Таймкод з, мс',
        'Kod czasowy od, ms',
        'Timecode de la, ms',
        'Código de tiempo desde, ms',
        'Таймкод с, мс',
      ),
    },
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    // A HUD continued from an earlier scene: it is already there, the timecode keeps counting
    const from = Math.max(0, Math.round(num(ctx, 'from', 0)));
    const enter = (a: FxAnim): FxAnim[] => (from > 0 ? [] : [a]);
    const m = (Math.min(W, H) * num(ctx, 'inset', 4)) / 100;
    const L = Math.min(W, H) * 0.045;
    const t = Math.max(1.5, 2.4 * u);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const a = clamp(num(ctx, 'opacity', 0.85), 0.05, 1);
    const fs = Math.max(9, 15 * u);
    const pad = L * 0.42;
    const line = `${px(t)} solid ${c}`;
    const out: FxNode[] = [];
    const corners: Array<[string, string, string, string, number, number]> = [
      ['left', 'top', 'borderLeft', 'borderTop', -1, -1],
      ['right', 'top', 'borderRight', 'borderTop', 1, -1],
      ['left', 'bottom', 'borderLeft', 'borderBottom', -1, 1],
      ['right', 'bottom', 'borderRight', 'borderBottom', 1, 1],
    ];
    corners.forEach(([h, v, b1, b2, sx, sy], i) =>
      out.push({
        style: {
          position: 'absolute',
          [h]: px(m),
          [v]: px(m),
          width: px(L),
          height: px(L),
          [b1]: line,
          [b2]: line,
          opacity: a,
        },
        anims: enter({
          delay: i * 60,
          duration: Math.min(700, D * 0.5),
          fill: 'both',
          easing: OUT,
          keyframes: [
            { opacity: 0, transform: `translate(${px(sx * L * 0.7)}, ${px(sy * L * 0.7)})` },
            { opacity: a, transform: 'translate(0px, 0px)' },
          ],
        }),
      }),
    );
    const text = (s: string) => ({
      text: s,
      style: monoText(ctx, fs, {
        color: c,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
      }),
    });
    const label = str(ctx, 'label', '');
    if (label)
      out.push({
        ...text(label),
        style: {
          ...text(label).style,
          position: 'absolute',
          left: px(m + pad),
          top: px(m + pad),
          opacity: a,
        },
        anims: enter(arrive(250, 500, -fs)),
      });
    const label2 = str(ctx, 'label2', '');
    if (label2)
      out.push({
        style: {
          position: 'absolute',
          right: px(m + pad),
          top: px(m + pad),
          display: 'flex',
          alignItems: 'center',
          gap: px(fs * 0.6),
          opacity: a,
        },
        children: [
          {
            style: {
              width: px(fs * 0.62),
              height: px(fs * 0.62),
              borderRadius: '50%',
              background: c2,
            },
            anims: [blink(D, 300)],
          },
          text(label2),
        ],
        anims: enter(arrive(300, 500, -fs)),
      });
    const label3 = str(ctx, 'label3', '');
    if (label3)
      out.push({
        ...text(label3),
        style: {
          ...text(label3).style,
          position: 'absolute',
          left: px(m + pad),
          bottom: px(m + pad),
          opacity: a,
        },
        anims: enter(arrive(350, 500, fs)),
      });
    if (ctx.params.timecode !== 'off') {
      const h = fs * 1.15;
      const plain = (s: string): FxNode => ({ text: s, style: { display: 'inline-block' } });
      out.push({
        style: monoText(ctx, fs, {
          position: 'absolute',
          right: px(m + pad),
          bottom: px(m + pad),
          display: 'flex',
          alignItems: 'center',
          height: px(h),
          lineHeight: px(h),
          color: c,
          letterSpacing: '0.06em',
          opacity: a,
        }),
        children: [
          plain('00:00:'),
          // Seconds and frames at 30 fps, counted from the start of the item
          reel(DIGITS.slice(0, 6), h, D, { cycle: 60_000, offset: from }),
          reel(DIGITS, h, D, { cycle: 10_000, offset: from }),
          plain(':'),
          reel(DIGITS.slice(0, 3), h, D, { cycle: 1000, offset: from }),
          reel(DIGITS, h, D, { cycle: 1000 / 3, offset: from }),
        ],
        anims: enter(arrive(400, 500, fs)),
      });
    }
    return out;
  },
};

const terminal: FxKind = {
  id: 'terminal',
  label: lbl(
    'Terminal prompt',
    'Термінал із запитом',
    'Terminal z poleceniem',
    'Terminal cu prompt',
    'Terminal con prompt',
    'Терминал с запросом',
  ),
  category: 'interface',
  z: 'front',
  duration: 3200,
  params: [
    P_LONGTEXT('make me a launch video'),
    {
      key: 'prompt',
      type: 'text',
      default: '>',
      label: lbl(
        'Prompt sign',
        'Знак запиту',
        'Znak zachęty',
        'Semn prompt',
        'Signo',
        'Знак запроса',
      ),
    },
    {
      key: 'style',
      type: 'select',
      default: 'plain',
      label: lbl('Style', 'Стиль', 'Styl', 'Stil', 'Estilo', 'Стиль'),
      options: [
        opt(
          'plain',
          lbl('Text only', 'Лише текст', 'Sam tekst', 'Doar text', 'Solo texto', 'Только текст'),
        ),
        opt('window', lbl('Window', 'Вікно', 'Okno', 'Fereastră', 'Ventana', 'Окно')),
      ],
    },
    {
      key: 'align',
      type: 'select',
      default: 'center',
      label: lbl('Align', 'Вирівнювання', 'Wyrównanie', 'Aliniere', 'Alineación', 'Выравнивание'),
      options: [
        opt('center', lbl('Centre', 'Центр', 'Środek', 'Centru', 'Centro', 'Центр')),
        opt('left', lbl('Left', 'Ліворуч', 'Do lewej', 'Stânga', 'Izquierda', 'Слева')),
      ],
    },
    P_SIZE(4, 1, 12),
    P_Y,
    P_SPEED(16),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    // From a text layer (its text, editable through the API) or the text of the effect
    const lines = ctx.params.source
      ? ctx.text
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .slice(0, 8)
      : linesOf(ctx, 'text', 'make me a launch video', 8);
    if (!lines.length) return [];
    const prompt = str(ctx, 'prompt', '>').slice(0, 4);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const window = ctx.params.style === 'window';
    const lead = prompt ? glyphs(prompt) + 1 : 0;
    const maxChars = Math.max(...lines.map(glyphs)) + lead + 2;
    let fs = (Math.min(W, H) * num(ctx, 'size', 4)) / 100;
    const room = W * (window ? 0.78 : 0.88);
    fs = Math.min(fs, room / (maxChars * 0.6), (H * 0.7) / (lines.length * 1.5 + (window ? 3 : 0)));
    const rowH = fs * 1.5;
    const boxW = maxChars * 0.6 * fs;
    const boxH = lines.length * rowH;
    // Line i starts once the previous one is typed, with a short pause
    const { starts, perChar: pc } = typingPlan(
      lines.map(glyphs),
      D,
      250,
      350,
      1000 / clamp(num(ctx, 'speed', 16), 2, 80),
    );
    const rows: FxNode[] = lines.map((l, i) => {
      const n = glyphs(l);
      const end = i < lines.length - 1 ? starts[i + 1] : D;
      return {
        style: { display: 'flex', alignItems: 'center', height: px(rowH), whiteSpace: 'pre' },
        anims: [showAt(starts[i] - 120)],
        children: <FxNode[]>[
          ...(prompt
            ? [{ text: `${prompt} `, style: { color: c2, whiteSpace: 'pre', fontWeight: 700 } }]
            : []),
          {
            text: l,
            style: { display: 'inline-block', overflow: 'hidden', whiteSpace: 'pre', width: '0ch' },
            anims: [typeAnim(n, starts[i], pc)],
          },
          {
            style: { display: 'inline-block', marginLeft: '0.12ch' },
            anims: [windowAnim(D, starts[i] - 120, end)],
            children: [
              {
                style: { width: '0.9ch', height: px(fs * 1.12), background: c2 },
                anims: [blink(D, starts[i] + n * pc, 900)],
              },
            ],
          },
        ],
      };
    });
    const align = ctx.params.align === 'left' ? 'left' : 'center';
    const cy = (H * clamp(num(ctx, 'y', 50), 0, 100)) / 100;
    const text: FxNode = {
      style: monoText(ctx, fs, {
        position: window ? 'relative' : 'absolute',
        ...(window
          ? {}
          : {
              left: px(align === 'left' ? W * 0.08 : (W - boxW) / 2),
              top: px(clamp(cy - boxH / 2, 0, H - boxH)),
            }),
        width: px(boxW),
        color: c,
      }),
      children: rows,
    };
    if (!window) return [text];
    const padX = fs * 1.2;
    const bar = fs * 2;
    const panelW = boxW + padX * 2;
    const panelH = boxH + bar + fs * 1.2;
    const bg = shade(ctx.palette.dark, 0.06);
    return [
      {
        style: {
          position: 'absolute',
          left: px(align === 'left' ? W * 0.06 : (W - panelW) / 2),
          top: px(clamp(cy - panelH / 2, 0, H - panelH)),
          width: px(panelW),
          height: px(panelH),
          background: withAlpha(bg, 0.94),
          border: `1px solid ${withAlpha(ctx.palette.light, 0.12)}`,
          borderRadius: px(fs * 0.6),
          boxShadow: `0 ${px(fs)} ${px(fs * 3)} ${withAlpha('#000000', 0.35)}`,
          overflow: 'hidden',
        },
        anims: [
          {
            delay: 0,
            duration: 450,
            fill: 'both',
            easing: OUT,
            keyframes: [
              { opacity: 0, transform: 'scale(0.94)' },
              { opacity: 1, transform: 'scale(1)' },
            ],
          },
        ],
        children: [
          {
            style: {
              height: px(bar),
              display: 'flex',
              alignItems: 'center',
              gap: px(fs * 0.45),
              padding: `0 ${px(padX * 0.7)}`,
              borderBottom: `1px solid ${withAlpha(ctx.palette.light, 0.08)}`,
            },
            children: ['#FF5F57', '#FEBC2E', '#28C840'].map((dot) => ({
              style: {
                width: px(fs * 0.5),
                height: px(fs * 0.5),
                borderRadius: '50%',
                background: dot,
              },
            })),
          },
          {
            style: { padding: `${px(fs * 0.6)} ${px(padX)}` },
            children: [text],
          },
        ],
      },
    ];
  },
};

/** Rough syntax colouring of one line of code: [text, kind] pieces. */
type Tok = 'kw' | 'str' | 'tag' | 'num' | 'punct' | 'id' | 'sp';
function tokens(line: string): Array<[string, Tok]> {
  const re =
    /("[^"]*"?|'[^']*'?|`[^`]*`?|<\/?[A-Za-z][\w.]*|\b(?:export|default|const|let|var|return|import|from|function|async|await|if|else|for|while|new|class|extends|true|false|null|undefined|def|print|fn|pub|use)\b|\d+(?:\.\d+)?|\s+|[{}()[\]=<>/;:,.+\-*!?&|]+|[^\s"'`<>{}()[\]=;:,.+\-*!?&|/]+)/g;
  const out: Array<[string, Tok]> = [];
  for (const m of line.matchAll(re)) {
    const s = m[0];
    const k: Tok = /^\s+$/.test(s)
      ? 'sp'
      : /^["'`]/.test(s)
        ? 'str'
        : /^<\/?[A-Za-z]/.test(s)
          ? 'tag'
          : /^\d/.test(s)
            ? 'num'
            : /^[{}()[\]=<>/;:,.+\-*!?&|]+$/.test(s)
              ? 'punct'
              : /^(?:export|default|const|let|var|return|import|from|function|async|await|if|else|for|while|new|class|extends|true|false|null|undefined|def|print|fn|pub|use)$/.test(
                    s,
                  )
                ? 'kw'
                : 'id';
    out.push([s, k]);
  }
  return out;
}

const DEFAULT_CODE = [
  'export const Launch = () => (',
  '  <Scene fps={60} size="16:9">',
  '    <Fill color="#FF3C00" />',
  '    <Title text="NEW" />',
  '    <Product src="sneaker.png" spin />',
  '  </Scene>',
  ');',
].join('\n');

const code: FxKind = {
  id: 'code',
  label: lbl(
    'Code editor',
    'Редактор коду',
    'Edytor kodu',
    'Editor de cod',
    'Editor de código',
    'Редактор кода',
  ),
  category: 'interface',
  z: 'front',
  duration: 4000,
  params: [
    {
      key: 'text',
      type: 'longtext',
      default: DEFAULT_CODE,
      label: lbl('Code', 'Код', 'Kod', 'Cod', 'Código', 'Код'),
    },
    {
      key: 'title',
      type: 'text',
      default: 'launch.tsx',
      label: lbl('File name', 'Назва файлу', 'Nazwa pliku', 'Nume fișier', 'Archivo', 'Имя файла'),
    },
    {
      key: 'tilt',
      type: 'select',
      default: 'tilt',
      label: lbl('Angle', 'Ракурс', 'Ujęcie', 'Unghi', 'Ángulo', 'Ракурс'),
      options: [
        opt('tilt', lbl('Tilted', 'Нахилений', 'Pochylony', 'Înclinat', 'Inclinado', 'Наклон')),
        opt(
          'settle',
          lbl(
            'Tilt to flat',
            'Вирівнюється',
            'Prostuje się',
            'Se îndreaptă',
            'Se endereza',
            'Выравнивается',
          ),
        ),
        opt('flat', lbl('Flat', 'Прямо', 'Płasko', 'Drept', 'Plano', 'Прямо')),
      ],
    },
    P_SIZE(3.6, 1, 8),
    P_SPEED(30),
    P_COLOR('bg', 'dark', lbl('Background', 'Фон', 'Tło', 'Fundal', 'Fondo', 'Фон')),
    P_COLOR2('accent'),
    P_COLOR3('accent2'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const lines = str(ctx, 'text', DEFAULT_CODE)
      .replace(/\t/g, '  ')
      .split('\n')
      .map((l) => l.replace(/\s+$/, ''))
      .slice(0, 14);
    if (!lines.some((l) => l.trim())) return [];
    const bg = color(ctx, 'bg', 'dark');
    const ink = readable(bg, ctx.palette.light, '#F5F5F7', ctx.palette.dark);
    const c2 = readable(bg, color(ctx, 'color2', 'accent'), shade(ctx.palette.accent, 0.4), ink);
    const c3 = readable(bg, color(ctx, 'color3', 'accent2'), shade(ctx.palette.accent2, 0.5), ink);
    const maxChars = Math.max(...lines.map(glyphs)) + 6;
    const head = 2.6;
    let fs = (Math.min(W, H) * num(ctx, 'size', 3.6)) / 100;
    fs = Math.min(
      fs,
      (W * 0.84) / (maxChars * 0.6 + 2.4),
      (H * 0.78) / (lines.length * 1.7 + head + 1.6),
    );
    const rowH = fs * 1.7;
    const padX = fs * 1.2;
    const panelW = maxChars * 0.6 * fs + padX * 2;
    const panelH = lines.length * rowH + fs * head + fs * 1.6;
    const { starts, perChar } = typingPlan(
      lines.map((l) => Math.max(1, glyphs(l.trimStart()))),
      D,
      400,
      140,
      1000 / clamp(num(ctx, 'speed', 30), 2, 80),
    );
    const tint: Record<Tok, string> = {
      kw: c2,
      str: c3,
      num: c3,
      tag: ink,
      punct: withAlpha(ink, 0.5),
      id: withAlpha(ink, 0.88),
      sp: ink,
    };
    const rows: FxNode[] = lines.map((l, i) => {
      const indent = glyphs(l) - glyphs(l.trimStart());
      const body = l.trimStart();
      const n = glyphs(body);
      const end = i < lines.length - 1 ? starts[i + 1] : D;
      return {
        style: {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          height: px(rowH),
          whiteSpace: 'pre',
        },
        children: <FxNode[]>[
          {
            // Current line highlight
            style: {
              position: 'absolute',
              left: px(-padX),
              right: px(-padX),
              top: '0px',
              bottom: '0px',
              background: withAlpha(c2, 0.16),
              borderLeft: `${px(Math.max(2, fs * 0.15))} solid ${c2}`,
            },
            anims: [windowAnim(D, starts[i], end)],
          },
          {
            text: String(i + 1),
            style: {
              position: 'relative',
              width: '3ch',
              marginRight: '2ch',
              textAlign: 'right',
              color: withAlpha(ink, 0.32),
            },
            anims: [showAt(starts[i] - 60)],
          },
          { text: ' '.repeat(indent), style: { position: 'relative', whiteSpace: 'pre' } },
          {
            style: {
              position: 'relative',
              display: 'inline-block',
              overflow: 'hidden',
              whiteSpace: 'pre',
              width: '0ch',
            },
            anims: [typeAnim(Math.max(1, n), starts[i], perChar)],
            children: tokens(body).map(([s, k]) => ({
              text: s,
              style: {
                display: 'inline',
                color: tint[k],
                fontWeight: k === 'kw' || k === 'tag' ? 700 : 500,
              },
            })),
          },
          {
            style: { position: 'relative', display: 'inline-block', marginLeft: '0.1ch' },
            anims: [windowAnim(D, starts[i], end)],
            children: [
              {
                style: { width: '0.85ch', height: px(fs * 1.15), background: c2 },
                anims: [blink(D, starts[i] + n * perChar, 900)],
              },
            ],
          },
        ],
      };
    });
    const title = str(ctx, 'title', 'launch.tsx').slice(0, 40);
    const tilt = str(ctx, 'tilt', 'tilt');
    const persp = `perspective(${px(Math.max(W, H) * 1.6)})`;
    const tilted = `${persp} rotateX(16deg) rotateY(-20deg) rotateZ(4deg)`;
    const flat = `${persp} rotateX(0deg) rotateY(0deg) rotateZ(0deg)`;
    const tiltAnims: FxAnim[] =
      tilt === 'settle'
        ? [
            {
              delay: 0,
              duration: Math.min(D * 0.7, 2600),
              fill: 'both',
              easing: OUT,
              keyframes: [{ transform: tilted }, { transform: flat }],
            },
          ]
        : tilt === 'tilt'
          ? [
              {
                delay: 0,
                duration: D,
                fill: 'both',
                easing: 'linear',
                keyframes: [
                  { transform: tilted },
                  {
                    transform: `${persp} rotateX(12deg) rotateY(-12deg) rotateZ(2deg)`,
                  },
                ],
              },
            ]
          : [];
    return [
      {
        style: {
          position: 'absolute',
          left: px((W - panelW) / 2),
          top: px((H - panelH) / 2),
          width: px(panelW),
          height: px(panelH),
        },
        anims: [arrive(0, 700, fs * 2)],
        children: [
          {
            style: monoText(ctx, fs, {
              width: '100%',
              height: '100%',
              background: bg,
              color: ink,
              border: `1px solid ${withAlpha(ink, 0.12)}`,
              borderRadius: px(fs * 0.7),
              boxShadow: `0 ${px(fs * 1.5)} ${px(fs * 4)} ${withAlpha('#000000', 0.4)}`,
              overflow: 'hidden',
              transform: tilt === 'flat' ? 'none' : tilted,
            }),
            anims: tiltAnims,
            children: [
              {
                style: {
                  height: px(fs * head),
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: px(fs * 1.6),
                  padding: `0 ${px(padX)}`,
                  borderBottom: `1px solid ${withAlpha(ink, 0.08)}`,
                  fontSize: px(fs * 0.85),
                },
                children: [
                  {
                    text: title,
                    style: {
                      padding: `0 0 ${px(fs * 0.55)}`,
                      borderBottom: `${px(Math.max(2, fs * 0.14))} solid ${c2}`,
                      color: ink,
                    },
                  },
                  {
                    text: 'motion.ts',
                    style: { padding: `0 0 ${px(fs * 0.55)}`, color: withAlpha(ink, 0.4) },
                  },
                ],
              },
              { style: { padding: `${px(fs * 0.8)} ${px(padX)}` }, children: rows },
            ],
          },
        ],
      },
    ];
  },
};

const counter: FxKind = {
  id: 'counter',
  label: lbl('Counter', 'Лічильник', 'Licznik', 'Contor', 'Contador', 'Счётчик'),
  category: 'interface',
  z: 'front',
  duration: 2400,
  params: [
    {
      key: 'from',
      type: 'number',
      default: 0,
      min: 0,
      max: 999_999,
      label: lbl('From', 'Від', 'Od', 'De la', 'Desde', 'От'),
    },
    {
      key: 'to',
      type: 'number',
      default: 48,
      min: 0,
      max: 999_999,
      label: lbl('To', 'До', 'Do', 'Până la', 'Hasta', 'До'),
    },
    {
      key: 'prefix',
      type: 'text',
      default: '+',
      label: lbl('Before', 'Перед числом', 'Przed', 'Înainte', 'Antes', 'Перед числом'),
    },
    {
      key: 'suffix',
      type: 'text',
      default: '%',
      label: lbl('After', 'Після числа', 'Po', 'După', 'Después', 'После числа'),
    },
    {
      key: 'label',
      type: 'text',
      default: '',
      label: lbl('Caption', 'Підпис', 'Podpis', 'Legendă', 'Leyenda', 'Подпись'),
    },
    onOff(
      'bar',
      'off',
      lbl(
        'Progress bar',
        'Смуга прогресу',
        'Pasek postępu',
        'Bară de progres',
        'Barra de progreso',
        'Полоса прогресса',
      ),
    ),
    P_SIZE(20, 3, 50),
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const from = Math.round(clamp(num(ctx, 'from', 0), 0, 999_999));
    const to = Math.round(clamp(num(ctx, 'to', 48), 0, 999_999));
    const prefix = String(ctx.params.prefix ?? '+').slice(0, 6);
    const suffix = String(ctx.params.suffix ?? '%').slice(0, 6);
    const caption = str(ctx, 'label', '');
    const bar = ctx.params.bar === 'on';
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const nd = Math.max(String(from).length, String(to).length);
    const chars = nd + glyphs(prefix) + glyphs(suffix);
    let fs = (Math.min(W, H) * num(ctx, 'size', 20)) / 100;
    fs = Math.min(fs, (W * 0.86) / (chars * 0.62));
    const h = fs * 1.02;
    const run = Math.max(300, D * 0.72);
    const columns = countUp(from, to, run, D, h);
    const cx = (W * clamp(num(ctx, 'x', 50), 0, 100)) / 100;
    const cy = (H * clamp(num(ctx, 'y', 50), 0, 100)) / 100;
    const font = ctx.font;
    const numberRow: FxNode = {
      style: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        height: px(h),
        lineHeight: px(h),
        fontFamily: font.family,
        fontWeight: Math.max(700, font.weight),
        fontStyle: font.style,
        fontSize: px(fs),
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.02em',
        color: c,
        whiteSpace: 'pre',
      },
      children: [
        ...(prefix ? [{ text: prefix, style: { display: 'inline-block' } }] : []),
        ...columns,
        ...(suffix ? [{ text: suffix, style: { display: 'inline-block' } }] : []),
      ],
    };
    const blockW = Math.min(W * 0.9, Math.max(chars * 0.62 * fs, fs * 3));
    const children: FxNode[] = [numberRow];
    if (caption)
      children.push({
        text: caption,
        style: monoText(ctx, Math.max(10, fs * 0.13), {
          marginTop: px(fs * 0.18),
          color: withAlpha(c, 0.7),
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: '100%',
        }),
      });
    if (bar) {
      const target = to <= 100 ? to / 100 : 1;
      const start = to <= 100 ? from / 100 : 0;
      children.push({
        style: {
          position: 'relative',
          width: '100%',
          height: px(Math.max(3, fs * 0.05)),
          marginTop: px(fs * 0.2),
          background: withAlpha(c, 0.15),
          borderRadius: px(fs),
          overflow: 'hidden',
        },
        children: [
          {
            style: {
              position: 'absolute',
              left: '0px',
              top: '0px',
              bottom: '0px',
              width: '100%',
              background: c2,
              transformOrigin: '0% 50%',
            },
            anims: [
              {
                delay: 0,
                duration: run,
                fill: 'both',
                easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
                keyframes: [
                  { transform: `scaleX(${r2(clamp01(start))})` },
                  { transform: `scaleX(${r2(clamp01(target))})` },
                ],
              },
            ],
          },
        ],
      });
    }
    const blockH = h + (caption ? fs * 0.35 : 0) + (bar ? fs * 0.3 : 0);
    return [
      {
        style: {
          position: 'absolute',
          left: px(clamp(cx - blockW / 2, 0, Math.max(0, W - blockW))),
          top: px(clamp(cy - blockH / 2, 0, Math.max(0, H - blockH))),
          width: px(blockW),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        },
        anims: [arrive(0, 450, fs * 0.2)],
        children,
      },
    ];
  },
};

const bars: FxKind = {
  id: 'bars',
  label: lbl(
    'Bar chart',
    'Стовпчикова діаграма',
    'Wykres słupkowy',
    'Grafic cu bare',
    'Gráfico de barras',
    'Столбчатая диаграмма',
  ),
  category: 'interface',
  z: 'front',
  duration: 2400,
  params: [
    {
      key: 'values',
      type: 'text',
      default: '3, 4, 5, 4, 6, 9',
      label: lbl('Values', 'Значення', 'Wartości', 'Valori', 'Valores', 'Значения'),
    },
    {
      key: 'label',
      type: 'text',
      default: '',
      label: lbl('Caption', 'Підпис', 'Podpis', 'Legendă', 'Leyenda', 'Подпись'),
    },
    P_SIZE(56, 10, 95),
    {
      key: 'height',
      type: 'number',
      default: 36,
      min: 5,
      max: 90,
      label: lbl('Height, %', 'Висота, %', 'Wysokość, %', 'Înălțime, %', 'Altura, %', 'Высота, %'),
    },
    P_X,
    { ...P_Y, default: 55 },
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const values = String(ctx.params.values ?? '')
      .split(/[,;\s]+/)
      .map(Number)
      .filter((v) => Number.isFinite(v) && v >= 0)
      .slice(0, 16);
    const list = values.length ? values : [3, 4, 5, 4, 6, 9];
    const max = Math.max(...list, 1e-6);
    const n = list.length;
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const w = (W * clamp(num(ctx, 'size', 56), 10, 95)) / 100;
    const h = (H * clamp(num(ctx, 'height', 36), 5, 90)) / 100;
    const cx = (W * clamp(num(ctx, 'x', 50), 0, 100)) / 100;
    const cy = (H * clamp(num(ctx, 'y', 55), 0, 100)) / 100;
    const gap = w / n / 3.2;
    const bw = (w - gap * (n - 1)) / n;
    const grow = Math.min(650, D * 0.4);
    const stagger = Math.max(0, Math.min(140, (D * 0.85 - 200 - grow) / n));
    const left = clamp(cx - w / 2, 0, W - w);
    const top = clamp(cy - h / 2, 0, H - h);
    const out: FxNode[] = list.map((v, i) => ({
      style: {
        position: 'absolute',
        left: px(left + i * (bw + gap)),
        top: px(top + h - (h * v) / max),
        width: px(bw),
        height: px(Math.max(1, (h * v) / max)),
        background: i === n - 1 ? c2 : c,
        borderRadius: px(Math.min(bw * 0.12, 8 * unit(ctx))),
        transformOrigin: '50% 100%',
      },
      anims: [
        {
          delay: 200 + i * stagger,
          duration: grow,
          fill: 'both',
          easing: 'cubic-bezier(0.34, 1.35, 0.64, 1)',
          keyframes: [{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }],
        },
      ],
    }));
    out.push({
      style: {
        position: 'absolute',
        left: px(left - gap),
        top: px(top + h),
        width: px(w + gap * 2),
        height: px(Math.max(1, 2 * unit(ctx))),
        background: withAlpha(c, 0.3),
        transformOrigin: '0% 50%',
      },
      anims: [
        {
          delay: 0,
          duration: 500,
          fill: 'both',
          easing: OUT,
          keyframes: [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        },
      ],
    });
    const caption = str(ctx, 'label', '');
    if (caption) {
      const fs = Math.max(10, 16 * unit(ctx));
      out.push({
        text: caption,
        style: monoText(ctx, fs, {
          position: 'absolute',
          left: px(left),
          top: px(Math.min(H - fs * 1.5, top + h + fs * 0.9)),
          width: px(w),
          color: withAlpha(c, 0.7),
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          overflow: 'hidden',
        }),
        anims: [arrive(Math.min(300 + n * stagger, D * 0.6), Math.min(400, D * 0.3), fs * 0.5)],
      });
    }
    return out;
  },
};

// ---------------------------------------------------------------- text

const words: FxKind = {
  id: 'words',
  label: lbl(
    'Kinetic words',
    'Кінетичні слова',
    'Kinetyczne słowa',
    'Cuvinte cinetice',
    'Palabras cinéticas',
    'Кинетические слова',
  ),
  category: 'text',
  z: 'front',
  duration: 2400,
  params: [
    { ...P_LONGTEXT(''), label: P_TEXT.label },
    {
      key: 'style',
      type: 'select',
      default: 'slam',
      label: lbl('Animation', 'Анімація', 'Animacja', 'Animație', 'Animación', 'Анимация'),
      options: [
        opt('slam', lbl('Slam', 'Удар', 'Uderzenie', 'Lovitură', 'Golpe', 'Удар')),
        opt(
          'blur',
          lbl(
            'Motion blur',
            'Розмиття руху',
            'Rozmycie ruchu',
            'Blur de mișcare',
            'Desenfoque',
            'Размытие движения',
          ),
        ),
        opt(
          'rise',
          lbl(
            'Mask rise',
            'Підйом з маски',
            'Wysunięcie',
            'Ridicare din mască',
            'Subida',
            'Подъём из маски',
          ),
        ),
        opt('stack', lbl('Stack up', 'Стовпчиком', 'Stos', 'Stivă', 'Apilar', 'Столбиком')),
      ],
    },
    {
      key: 'bg',
      type: 'select',
      default: 'flip',
      label: lbl('Background', 'Фон', 'Tło', 'Fundal', 'Fondo', 'Фон'),
      options: [
        opt(
          'flip',
          lbl(
            'Alternate colours',
            'Чергувати кольори',
            'Naprzemienne kolory',
            'Culori alternante',
            'Alternar colores',
            'Чередовать цвета',
          ),
        ),
        opt(
          'solid',
          lbl('One colour', 'Один колір', 'Jeden kolor', 'O culoare', 'Un color', 'Один цвет'),
        ),
        opt(
          'none',
          lbl(
            'Transparent',
            'Прозорий',
            'Przezroczyste',
            'Transparent',
            'Transparente',
            'Прозрачный',
          ),
        ),
      ],
    },
    P_SIZE(30, 5, 60),
    P_COLOR('color', 'light'),
    P_COLOR2('accent'),
    P_COLOR3('dark'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const raw = str(ctx, 'text', '');
    const items = raw.trim()
      ? linesOf(ctx, 'text', '', 10)
      : headlineWords(ctx, 6).map((w) => w.replace(/ /g, ' '));
    if (!items.length) return [];
    const style = str(ctx, 'style', 'slam');
    const bgMode = str(ctx, 'bg', 'flip');
    const n = items.length;
    const seg = D / n;
    const upper = ctx.font.transform === 'uppercase';
    const maxF = (Math.min(W, H) * num(ctx, 'size', 30)) / 100;
    const bgs = [color(ctx, 'color2', 'accent'), color(ctx, 'color3', 'dark')];
    const fg = color(ctx, 'color', 'light');
    const textStyle = (f: number, c: string): Record<string, string | number> => ({
      fontFamily: ctx.font.family,
      fontWeight: ctx.font.weight,
      fontStyle: ctx.font.style,
      textTransform: ctx.font.transform,
      fontSize: px(f),
      lineHeight: '0.95',
      letterSpacing: '-0.035em',
      color: c,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    });
    const out: FxNode[] = [];
    if (style === 'stack') {
      const f = Math.min(
        maxF,
        ...items.map((it) => fitFont([it], W * 0.86, H * 0.8, upper, maxF)),
        (H * 0.8) / (n * 1.0),
      );
      const bg = bgMode === 'none' ? null : bgs[0];
      if (bg) out.push({ style: full(ctx, { background: bg }), anims: [arrive(0, 250, 0)] });
      const c = bg ? readable(bg, fg, ctx.palette.light, ctx.palette.dark) : fg;
      out.push({
        style: full(ctx, {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }),
        children: items.map((it, i) => ({
          style: { overflow: 'hidden', paddingBottom: px(f * 0.06) },
          children: [
            {
              text: it,
              style: { ...textStyle(f, c), display: 'block' },
              anims: [
                {
                  delay: i * seg * 0.6,
                  duration: Math.min(600, seg),
                  fill: 'both',
                  easing: OUT,
                  keyframes: [{ transform: 'translateY(105%)' }, { transform: 'translateY(0%)' }],
                },
              ],
            },
          ],
        })),
      });
      return out;
    }
    items.forEach((it, i) => {
      const from = i * seg;
      const to = i === n - 1 ? D : (i + 1) * seg;
      const bg = bgMode === 'none' ? null : bgMode === 'solid' ? bgs[0] : bgs[i % 2];
      const c = bg ? readable(bg, fg, ctx.palette.light, ctx.palette.dark) : fg;
      const f = fitFont(it.split(' '), W * 0.86, H * 0.7, upper, maxF);
      const inDur = Math.max(120, Math.min(420, seg * 0.55));
      const enter: CssKeyframe[] =
        style === 'blur'
          ? [
              {
                transform: 'translateX(-14%) scaleX(1.25)',
                filter: `blur(${px(26 * u)})`,
                opacity: 0,
                easing: OUT,
              },
              { transform: 'translateX(0%) scaleX(1)', filter: 'blur(0px)', opacity: 1 },
            ]
          : style === 'rise'
            ? [{ transform: 'translateY(110%)', easing: OUT }, { transform: 'translateY(0%)' }]
            : [
                {
                  transform: 'scale(1.45)',
                  filter: `blur(${px(10 * u)})`,
                  easing: 'cubic-bezier(0.2, 0.9, 0.25, 1)',
                },
                { transform: 'scale(0.97)', filter: 'blur(0px)', offset: 0.6, easing: 'ease-out' },
                { transform: 'scale(1)', filter: 'blur(0px)' },
              ];
      out.push({
        style: full(ctx, {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(bg ? { background: bg } : {}),
        }),
        anims: [windowAnim(D, from, to)],
        children: [
          {
            style: {
              overflow: style === 'rise' ? 'hidden' : 'visible',
              maxWidth: px(W * 0.9),
              padding: `${px(f * 0.04)} ${px(f * 0.08)}`,
            },
            children: [
              {
                text: it,
                style: { ...textStyle(f, c), display: 'block', whiteSpace: 'normal' },
                anims: [{ delay: from, duration: inDur, fill: 'both', keyframes: enter }],
              },
            ],
          },
        ],
      });
    });
    return out;
  },
};

const sliceText: FxKind = {
  id: 'slice-text',
  label: lbl(
    'Sliced title',
    'Нарізаний заголовок',
    'Pocięty tytuł',
    'Titlu feliat',
    'Título en franjas',
    'Нарезанный заголовок',
  ),
  category: 'text',
  z: 'front',
  duration: 1600,
  params: [
    P_TEXT,
    P_COUNT(7, 3, 16),
    {
      key: 'mode',
      type: 'select',
      default: 'in',
      label: lbl('Mode', 'Режим', 'Tryb', 'Mod', 'Modo', 'Режим'),
      options: [
        opt('in', lbl('Assemble', 'Зібрати', 'Złóż', 'Asamblează', 'Ensamblar', 'Собрать')),
        opt(
          'in-out',
          lbl(
            'Assemble and break',
            'Зібрати й розбити',
            'Złóż i rozbij',
            'Asamblează și sparge',
            'Ensamblar y romper',
            'Собрать и разбить',
          ),
        ),
      ],
    },
    onOff(
      'lines',
      'on',
      lbl(
        'Speed lines',
        'Лінії швидкості',
        'Linie prędkości',
        'Linii de viteză',
        'Líneas de velocidad',
        'Линии скорости',
      ),
    ),
    P_SIZE(22, 5, 50),
    P_COLOR('color', 'ink'),
    P_COLOR2('ink'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const rnd = rng(ctx.seed);
    const text = str(ctx, 'text', ctx.text || 'MOTION')
      .replace(/\s+/g, ' ')
      .trim();
    const n = Math.round(clamp(num(ctx, 'count', 7), 3, 16));
    const upper = ctx.font.transform === 'uppercase';
    const f = fitFont(
      [text],
      W * 0.9,
      H * 0.5,
      upper,
      (Math.min(W, H) * num(ctx, 'size', 22)) / 100,
    );
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'ink');
    const bandTop = H / 2 - f * 0.62;
    const bandH = f * 1.24;
    const inDur = D * (ctx.params.mode === 'in-out' ? 0.42 : 0.6);
    const out: FxNode[] = [];
    for (let k = 0; k < n; k++) {
      const top = ((bandTop + (bandH * k) / n) / H) * 100;
      const bottom = 100 - ((bandTop + (bandH * (k + 1)) / n) / H) * 100;
      const side = k % 2 ? 1 : -1;
      const dx = side * W * (0.35 + rnd() * 0.5);
      const delay = rnd() * D * 0.18;
      const anims: FxAnim[] = [
        {
          delay,
          duration: inDur,
          fill: 'both',
          easing: OUT,
          keyframes: [
            { transform: `translateX(${px(dx)})`, filter: `blur(${px(6 * u)})` },
            { transform: 'translateX(0px)', filter: 'blur(0px)' },
          ],
        },
      ];
      if (ctx.params.mode === 'in-out')
        anims.push({
          delay: D * 0.72 + rnd() * D * 0.08,
          duration: D * 0.2,
          fill: 'forwards',
          easing: 'cubic-bezier(0.7, 0, 0.84, 0)',
          keyframes: [
            { transform: 'translateX(0px)' },
            { transform: `translateX(${px(-dx * 1.4)})` },
          ],
        });
      out.push({
        style: full(ctx, {
          clipPath: `inset(${r2(top)}% -5% ${r2(bottom)}% -5%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }),
        children: [
          {
            text,
            style: {
              fontFamily: ctx.font.family,
              fontWeight: ctx.font.weight,
              fontStyle: ctx.font.style,
              textTransform: ctx.font.transform,
              fontSize: px(f),
              lineHeight: '1',
              letterSpacing: '-0.03em',
              whiteSpace: 'nowrap',
              color: c,
            },
          },
        ],
        anims,
      });
    }
    if (ctx.params.lines !== 'off') {
      const m = Math.max(3, Math.round(n * 0.7));
      for (let i = 0; i < m; i++) {
        const y = bandTop + rnd() * bandH;
        const len = W * (0.25 + rnd() * 0.5);
        const th = Math.max(2, (3 + rnd() * 6) * u);
        const dir = i % 2 ? 1 : -1;
        out.push({
          style: {
            position: 'absolute',
            left: '0px',
            top: px(y),
            width: px(len),
            height: px(th),
            background: c2,
          },
          anims: [
            {
              delay: rnd() * D * 0.3,
              duration: D * (0.3 + rnd() * 0.2),
              fill: 'both',
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              keyframes: [
                { transform: `translateX(${px(dir > 0 ? -len : W)})` },
                { transform: `translateX(${px(dir > 0 ? W : -len)})` },
              ],
            },
          ],
        });
      }
    }
    return out;
  },
};

const textRings: FxKind = {
  id: 'text-rings',
  label: lbl(
    'Rings of text',
    'Кільця тексту',
    'Pierścienie tekstu',
    'Inele de text',
    'Anillos de texto',
    'Кольца текста',
  ),
  category: 'pattern',
  z: 'front',
  duration: 3000,
  params: [
    P_LONGTEXT('Motion design\nAnimation\nTypography\nArt direction'),
    P_COUNT(3, 1, 6),
    P_SIZE(42, 10, 60),
    P_TURNS(0.25),
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const lines = linesOf(ctx, 'text', 'Motion design\nAnimation\nTypography\nArt direction', 12);
    if (!lines.length) return [];
    const n = Math.round(clamp(num(ctx, 'count', 3), 1, 6));
    const R = (Math.min(W, H) * clamp(num(ctx, 'size', 42), 10, 60)) / 100;
    const cx = (W * clamp(num(ctx, 'x', 50), 0, 100)) / 100;
    const cy = (H * clamp(num(ctx, 'y', 50), 0, 100)) / 100;
    const turns = clamp(num(ctx, 'turns', 0.25), -5, 5);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const out: FxNode[] = [];
    for (let j = 0; j < n; j++) {
      const r = R * (1 - (j * 0.62) / Math.max(1, n));
      const fs = Math.max(8, R * 0.11 * (1 - j * 0.12));
      // Repeat the line around the circle, separated by dots
      const piece = `${lines[j % lines.length].toUpperCase()} • `;
      const per = fs * 0.78;
      // Whole repetitions only, spread evenly around the circle
      const pieceChars = [...piece];
      const reps = Math.max(1, Math.round((2 * Math.PI * r) / per / pieceChars.length));
      const count = reps * pieceChars.length;
      const chars = Array.from({ length: count }, (_, i) => pieceChars[i % pieceChars.length]);
      const outline = j % 2 === 1;
      out.push({
        style: { position: 'absolute', left: px(cx), top: px(cy), width: '0px', height: '0px' },
        anims: [
          {
            delay: j * 140,
            duration: 800,
            fill: 'both',
            easing: OUT,
            keyframes: [
              { opacity: 0, transform: 'scale(0.7)' },
              { opacity: 1, transform: 'scale(1)' },
            ],
          },
        ],
        children: [
          {
            style: { position: 'absolute', left: '0px', top: '0px' },
            anims: [
              {
                delay: 0,
                duration: D,
                fill: 'both',
                easing: 'linear',
                keyframes: [
                  { transform: 'rotate(0deg)' },
                  { transform: `rotate(${deg((j % 2 ? -1 : 1) * turns * 360 * (1 + j * 0.25))})` },
                ],
              },
            ],
            children: chars.map((ch, i) => ({
              text: ch,
              style: {
                position: 'absolute',
                left: px(-fs / 2),
                top: '0px',
                width: px(fs),
                height: px(fs),
                lineHeight: '1',
                textAlign: 'center',
                transformOrigin: '50% 0%',
                transform: `rotate(${deg((360 * i) / count)}) translateY(${px(-r)})`,
                fontFamily: ctx.font.family,
                fontWeight: ctx.font.weight,
                fontSize: px(fs),
                color: ch === '•' ? c2 : outline ? 'transparent' : c,
                WebkitTextStroke: outline && ch !== '•' ? `${px(Math.max(1, fs * 0.04))} ${c}` : '',
                whiteSpace: 'pre',
              },
            })),
          },
        ],
      });
    }
    const dot = R * 0.11;
    out.push({
      style: {
        position: 'absolute',
        left: px(cx - dot / 2),
        top: px(cy - dot / 2),
        width: px(dot),
        height: px(dot),
        borderRadius: '50%',
        background: c2,
      },
      anims: [
        {
          delay: 0,
          duration: 700,
          fill: 'both',
          easing: BACK,
          keyframes: [{ transform: 'scale(0)' }, { transform: 'scale(1)' }],
        },
      ],
    });
    return out;
  },
};

// ---------------------------------------------------------------- patterns

const dotGrid: FxKind = {
  id: 'dot-grid',
  label: lbl(
    'Dot grid wave',
    'Сітка точок',
    'Siatka kropek',
    'Grilă de puncte',
    'Rejilla de puntos',
    'Сетка точек',
  ),
  category: 'pattern',
  z: 'back',
  duration: 4000,
  params: [
    {
      key: 'style',
      type: 'select',
      default: 'wave',
      label: lbl('Motion', 'Рух', 'Ruch', 'Mișcare', 'Movimiento', 'Движение'),
      options: [
        opt('wave', lbl('Wave', 'Хвиля', 'Fala', 'Val', 'Ola', 'Волна')),
        opt('ripple', lbl('Ripple', 'Брижі', 'Zmarszczki', 'Unde', 'Ondas', 'Круги по воде')),
        opt(
          'scan',
          lbl('Scan line', 'Сканування', 'Skanowanie', 'Scanare', 'Escaneo', 'Сканирование'),
        ),
        opt(
          'plane',
          lbl('3D floor', '3D-площина', 'Podłoga 3D', 'Plan 3D', 'Suelo 3D', '3D-плоскость'),
        ),
      ],
    },
    {
      key: 'count',
      type: 'number',
      default: 22,
      min: 6,
      max: 40,
      label: lbl('Columns', 'Стовпці', 'Kolumny', 'Coloane', 'Columnas', 'Столбцы'),
    },
    P_SIZE(22, 5, 80),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
    P_OPACITY(0.8),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const style = str(ctx, 'style', 'wave');
    const plane = style === 'plane';
    // The 3D floor is wider than the canvas (it recedes) and deeper than it is tall
    const GW = plane ? W * 2 : W;
    const GH = plane ? H * 1.5 : H;
    let cols = Math.round(clamp(num(ctx, 'count', 22), 6, 40)) * (plane ? 2 : 1);
    let cell = GW / cols;
    let rows = Math.ceil(GH / cell);
    while (cols * rows > 900) {
      cols -= 1;
      cell = GW / cols;
      rows = Math.ceil(GH / cell);
    }
    const d = Math.max(2, (cell * clamp(num(ctx, 'size', 22), 5, 80)) / 100);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const a = clamp(num(ctx, 'opacity', 0.8), 0.05, 1);
    const P = Math.min(D, style === 'scan' ? 2600 : 2200);
    const iterations = Math.ceil(D / P) + 2;
    const dots: FxNode[] = [];
    const maxDist = Math.hypot(GW / 2, (rows * cell) / 2);
    for (let r = 0; r < rows; r++)
      for (let q = 0; q < cols; q++) {
        const x = q * cell + cell / 2;
        const y = r * cell + cell / 2;
        const phase =
          style === 'ripple'
            ? Math.hypot(x - GW / 2, y - (rows * cell) / 2) / maxDist
            : style === 'scan'
              ? x / GW
              : (x / GW) * 0.8 + (y / (rows * cell)) * 0.35;
        const keyframes: CssKeyframe[] =
          style === 'scan'
            ? [
                { transform: 'scale(1)', background: c, offset: 0 },
                { transform: 'scale(1)', background: c, offset: 0.4 },
                { transform: 'scale(2)', background: c2, offset: 0.5 },
                { transform: 'scale(1)', background: c, offset: 0.62 },
                { transform: 'scale(1)', background: c, offset: 1 },
              ]
            : plane
              ? [
                  {
                    transform: `translateY(${px(cell * 0.6)})`,
                    background: c,
                    easing: 'ease-in-out',
                  },
                  {
                    transform: `translateY(${px(-cell * 0.6)})`,
                    background: c2,
                    easing: 'ease-in-out',
                  },
                  { transform: `translateY(${px(cell * 0.6)})`, background: c },
                ]
              : [
                  { transform: 'scale(0.55)', background: c, easing: 'ease-in-out' },
                  { transform: 'scale(1.6)', background: c2, easing: 'ease-in-out' },
                  { transform: 'scale(0.55)', background: c },
                ];
        dots.push({
          style: {
            position: 'absolute',
            left: px(x - d / 2),
            top: px(y - d / 2),
            width: px(d),
            height: px(d),
            borderRadius: '50%',
            background: c,
          },
          anims: [
            {
              delay: -phase * P * (style === 'scan' ? 1 : 1.4),
              duration: P,
              iterations,
              fill: 'both',
              easing: 'linear',
              keyframes,
            },
          ],
        });
      }
    return [
      {
        style: full(ctx, {
          opacity: a,
          ...(plane
            ? {
                left: px((W - GW) / 2),
                top: px(H * 0.32),
                width: px(GW),
                height: px(rows * cell),
                transformOrigin: '50% 0%',
                transform: `perspective(${px(H * 0.9)}) rotateX(64deg)`,
              }
            : {}),
        }),
        anims: [
          {
            delay: 0,
            duration: 600,
            fill: 'both',
            easing: OUT,
            keyframes: [{ opacity: 0 }, { opacity: a }],
          },
        ],
        children: dots,
      },
    ];
  },
};

const orbit: FxKind = {
  id: 'orbit',
  label: lbl(
    'Particle orbit',
    'Орбіта частинок',
    'Orbita cząstek',
    'Orbită de particule',
    'Órbita de partículas',
    'Орбита частиц',
  ),
  category: 'pattern',
  z: 'front',
  duration: 3000,
  params: [
    {
      key: 'shape',
      type: 'select',
      default: 'ring',
      label: lbl('Shape', 'Форма', 'Kształt', 'Formă', 'Forma', 'Форма'),
      options: [
        opt('ring', lbl('Ring', 'Кільце', 'Pierścień', 'Inel', 'Anillo', 'Кольцо')),
        opt('torus', lbl('3D torus', '3D-тор', 'Torus 3D', 'Tor 3D', 'Toro 3D', '3D-тор')),
        opt('pills', lbl('Pills', 'Капсули', 'Kapsułki', 'Capsule', 'Píldoras', 'Капсулы')),
        opt('rosette', lbl('Rosette', 'Розетка', 'Rozeta', 'Rozetă', 'Roseta', 'Розетка')),
      ],
    },
    P_COUNT(48, 3, 160),
    P_SIZE(28, 4, 60),
    P_TURNS(0.5),
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const shape = str(ctx, 'shape', 'ring');
    const n = Math.round(clamp(num(ctx, 'count', 48), 3, 160));
    const R = (Math.min(W, H) * clamp(num(ctx, 'size', 28), 4, 60)) / 100;
    const cx = (W * clamp(num(ctx, 'x', 50), 0, 100)) / 100;
    const cy = (H * clamp(num(ctx, 'y', 50), 0, 100)) / 100;
    const turns = clamp(num(ctx, 'turns', 0.5), -5, 5);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const spin = (k: number, delay = 0): FxAnim => ({
      delay,
      duration: Math.max(16, D - delay),
      fill: 'both',
      easing: 'linear',
      keyframes: [{ transform: 'rotate(0deg)' }, { transform: `rotate(${deg(turns * 360 * k)})` }],
    });
    const pivot = (
      children: FxNode[],
      anims: FxAnim[] = [],
      extra: Record<string, string> = {},
    ): FxNode => ({
      style: {
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '0px',
        height: '0px',
        ...extra,
      },
      children,
      anims,
    });
    const dot = (
      theta: number,
      r: number,
      size: number,
      fill: string,
      extra: Record<string, string | number> = {},
    ): FxNode => ({
      style: {
        position: 'absolute',
        left: px(-size / 2),
        top: px(-size / 2),
        width: px(size),
        height: px(size),
        borderRadius: '50%',
        background: fill,
        transform: `rotate(${deg(theta)}) translateX(${px(r)})`,
        ...extra,
      },
    });
    let body: FxNode;
    if (shape === 'pills') {
      const m = Math.min(n, 16);
      const pw = R * 0.34;
      const ph = R * 0.14;
      body = pivot(
        Array.from({ length: m }, (_, i) =>
          pivot(
            [
              {
                style: {
                  position: 'absolute',
                  left: px(-pw / 2),
                  top: px(-ph / 2),
                  width: px(pw),
                  height: px(ph),
                  borderRadius: px(ph / 2),
                  background: i % 2 ? c : c2,
                  transform: 'rotate(90deg)',
                },
              },
            ],
            [
              {
                delay: 150 + i * 60,
                duration: 900,
                fill: 'both',
                easing: BACK,
                keyframes: [
                  { transform: `rotate(${deg((360 * i) / m)}) translateX(0px) scale(0.3)` },
                  { transform: `rotate(${deg((360 * i) / m)}) translateX(${px(R)}) scale(1)` },
                ],
              },
            ],
          ),
        ),
        [spin(1, 0)],
      );
    } else if (shape === 'rosette') {
      const rings = 6;
      body = pivot(
        Array.from({ length: rings }, (_, j) => {
          const r = R * (0.3 + (0.7 * (j + 1)) / rings);
          const count = Math.max(8, Math.round((n / rings) * (0.6 + j * 0.25)));
          const size = Math.max(2, R * 0.045);
          return pivot(
            Array.from({ length: count }, (_, i) => {
              const th = (360 * i) / count;
              const petal = 0.75 + 0.35 * Math.abs(Math.cos(((th * Math.PI) / 180) * 3 + j * 0.5));
              return dot(th, r * petal, size, j % 2 ? c2 : c);
            }),
            [spin(j % 2 ? -1 - j * 0.15 : 1 + j * 0.15)],
          );
        }),
      );
    } else {
      const torus = shape === 'torus';
      const rings = torus ? 5 : 1;
      const kids: FxNode[] = [];
      for (let j = 0; j < rings; j++) {
        const rr = torus ? R * (0.78 + j * 0.11) : R;
        const count = torus ? Math.round(n / rings) + 4 : n;
        for (let i = 0; i < count; i++) {
          const th = (360 * i) / count + (torus ? j * 7 : 0);
          const size = Math.max(2, R * (torus ? 0.03 + rnd() * 0.025 : 0.05));
          kids.push(
            dot(th, rr, size, (i + j) % 7 === 0 ? c2 : c, {
              opacity: torus ? 0.55 + rnd() * 0.45 : 1,
            }),
          );
        }
      }
      const spinning = pivot(kids, [spin(1)]);
      body = torus
        ? pivot([spinning], [], {
            transform: `perspective(${px(R * 5)}) rotateX(56deg) rotateZ(-18deg)`,
          })
        : spinning;
    }
    return [
      {
        style: { position: 'absolute', left: px(cx), top: px(cy), width: '0px', height: '0px' },
        anims: [
          {
            delay: 0,
            duration: 800,
            fill: 'both',
            easing: BACK,
            keyframes: [
              { opacity: 0, transform: 'scale(0.2)' },
              { opacity: 1, transform: 'scale(1)' },
            ],
          },
        ],
        children: [body],
      },
    ];
  },
};

/** Ribbon paths in canvas px. */
function ribbonPath(shape: string, W: number, H: number): string {
  const p = (x: number, y: number) => `${r2(x * W)} ${r2(y * H)}`;
  switch (shape) {
    case 'wave': {
      const pts: string[] = [];
      for (let i = 0; i <= 60; i++) {
        const x = -0.06 + (1.12 * i) / 60;
        const y = 0.5 + 0.2 * Math.sin((i / 60) * Math.PI * 5);
        pts.push(p(x, y));
      }
      return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`;
    }
    case 'scurve':
      return `M ${p(-0.08, 0.22)} C ${p(0.55, -0.05)}, ${p(0.45, 1.05)}, ${p(1.08, 0.78)}`;
    case 'spiral': {
      const pts: string[] = [];
      const m = Math.min(W, H);
      for (let i = 0; i <= 120; i++) {
        const t = i / 120;
        const a = t * Math.PI * 6;
        const r = (0.04 + 0.4 * t) * m;
        pts.push(`${r2(W / 2 + Math.cos(a) * r)} ${r2(H / 2 + Math.sin(a) * r)}`);
      }
      return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`;
    }
    case 'zigzag': {
      const pts = [
        p(-0.05, 0.3),
        p(0.18, 0.7),
        p(0.38, 0.3),
        p(0.58, 0.7),
        p(0.78, 0.3),
        p(1.05, 0.66),
      ];
      return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`;
    }
    default:
      // A big loop: in from the bottom left, round the top and out to the right
      return `M ${p(-0.08, 0.86)} C ${p(0.22, 0.98)}, ${p(0.42, 0.7)}, ${p(0.5, 0.42)} C ${p(0.58, 0.12)}, ${p(0.82, 0.1)}, ${p(0.8, 0.36)} C ${p(0.78, 0.6)}, ${p(0.5, 0.62)}, ${p(0.46, 0.44)} C ${p(0.42, 0.24)}, ${p(0.7, 0.18)}, ${p(1.08, 0.3)}`;
  }
}

const ribbon: FxKind = {
  id: 'ribbon',
  label: lbl(
    'Striped ribbon',
    'Смугаста стрічка',
    'Pasiasta wstęga',
    'Panglică dungată',
    'Cinta a rayas',
    'Полосатая лента',
  ),
  category: 'pattern',
  z: 'front',
  duration: 2400,
  params: [
    {
      key: 'shape',
      type: 'select',
      default: 'loop',
      label: lbl('Path', 'Траєкторія', 'Ścieżka', 'Traseu', 'Trayectoria', 'Траектория'),
      options: [
        opt('loop', lbl('Loop', 'Петля', 'Pętla', 'Buclă', 'Bucle', 'Петля')),
        opt('wave', lbl('Wave', 'Хвиля', 'Fala', 'Val', 'Ola', 'Волна')),
        opt('scurve', lbl('S-curve', 'S-крива', 'Krzywa S', 'Curbă S', 'Curva S', 'S-кривая')),
        opt('spiral', lbl('Spiral', 'Спіраль', 'Spirala', 'Spirală', 'Espiral', 'Спираль')),
        opt('zigzag', lbl('Zigzag', 'Зигзаг', 'Zygzak', 'Zigzag', 'Zigzag', 'Зигзаг')),
      ],
    },
    {
      key: 'draw',
      type: 'select',
      default: 'draw',
      label: lbl('Animation', 'Анімація', 'Animacja', 'Animație', 'Animación', 'Анимация'),
      options: [
        opt('draw', lbl('Draw on', 'Намалювати', 'Rysowanie', 'Desenare', 'Dibujar', 'Прорисовка')),
        opt(
          'draw-out',
          lbl(
            'Draw on and off',
            'Намалювати й стерти',
            'Narysuj i zetrzyj',
            'Desenează și șterge',
            'Dibujar y borrar',
            'Нарисовать и стереть',
          ),
        ),
        opt(
          'travel',
          lbl('Travel along', 'Пробігти', 'Przebiegnij', 'Parcurge', 'Recorrer', 'Пробежать'),
        ),
      ],
    },
    {
      key: 'width',
      type: 'number',
      default: 6,
      min: 1,
      max: 20,
      step: 0.5,
      label: lbl(
        'Thickness, %',
        'Товщина, %',
        'Grubość, %',
        'Grosime, %',
        'Grosor, %',
        'Толщина, %',
      ),
    },
    P_COLOR('color', 'dark'),
    P_COLOR2('light'),
    P_COLOR3('accent'),
    P_COLOR(
      'color4',
      'accent2',
      lbl(
        'Centre stripe',
        'Центральна смуга',
        'Środkowy pas',
        'Dunga centrală',
        'Franja central',
        'Центральная полоса',
      ),
    ),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const d = ribbonPath(str(ctx, 'shape', 'loop'), W, H);
    const w = (Math.min(W, H) * clamp(num(ctx, 'width', 6), 1, 20)) / 100;
    const stripes: Array<[string, number]> = [
      [color(ctx, 'color', 'dark'), 1],
      [color(ctx, 'color2', 'light'), 0.8],
      [color(ctx, 'color3', 'accent'), 0.56],
      [color(ctx, 'color2', 'light'), 0.36],
      [color(ctx, 'color4', 'accent2'), 0.16],
    ];
    const mode = str(ctx, 'draw', 'draw');
    const anim = (): FxAnim =>
      mode === 'travel'
        ? {
            target: 'path',
            delay: 0,
            duration: D,
            fill: 'both',
            easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
            keyframes: [
              { strokeDasharray: '0.4 2', strokeDashoffset: 0.4 },
              { strokeDasharray: '0.4 2', strokeDashoffset: -1 },
            ],
          }
        : mode === 'draw-out'
          ? {
              target: 'path',
              delay: 0,
              duration: D,
              fill: 'both',
              easing: 'linear',
              keyframes: [
                { strokeDashoffset: 1, offset: 0, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
                { strokeDashoffset: 0, offset: 0.48 },
                { strokeDashoffset: 0, offset: 0.6, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
                { strokeDashoffset: -1, offset: 1 },
              ],
            }
          : {
              target: 'path',
              delay: 0,
              duration: Math.max(300, D * 0.75),
              fill: 'both',
              easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
              keyframes: [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
            };
    return stripes.map(([stroke, k], i) => ({
      style: full(
        ctx,
        i === 0
          ? { filter: `drop-shadow(0 ${px(w * 0.35)} ${px(w * 0.45)} rgba(0, 0, 0, 0.22))` }
          : {},
      ),
      path: { d, stroke, width: Math.max(1, w * k), cap: 'round' },
      anims: [anim()],
    }));
  },
};

// ---------------------------------------------------------------- accents

const brackets: FxKind = {
  id: 'brackets',
  label: lbl(
    'Focus brackets',
    'Рамка фокусу',
    'Ramka ostrości',
    'Cadru de focalizare',
    'Corchetes de enfoque',
    'Рамка фокуса',
  ),
  category: 'accent',
  z: 'front',
  duration: 1600,
  params: [
    P_TARGET,
    {
      key: 'label',
      type: 'text',
      default: '',
      label: lbl('Label', 'Підпис', 'Etykieta', 'Etichetă', 'Etiqueta', 'Подпись'),
    },
    {
      key: 'pad',
      type: 'number',
      default: 10,
      min: 0,
      max: 60,
      label: lbl('Padding, %', 'Відступ, %', 'Odstęp, %', 'Spațiu, %', 'Margen, %', 'Отступ, %'),
    },
    P_COLOR('color', 'ink'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const b = ctx.box ?? { x: W * 0.3, y: H * 0.4, width: W * 0.4, height: H * 0.2 };
    const pad = (Math.min(b.width, b.height) * clamp(num(ctx, 'pad', 10), 0, 60)) / 100;
    const x0 = b.x - pad;
    const y0 = b.y - pad;
    const x1 = b.x + b.width + pad;
    const y1 = b.y + b.height + pad;
    const L = clamp(Math.min(x1 - x0, y1 - y0) * 0.22, 10 * u, 90 * u);
    const t = Math.max(2, 4 * u);
    const c = color(ctx, 'color', 'ink');
    const spread = L * 0.9;
    const line = `${px(t)} solid ${c}`;
    const corners: Array<[number, number, string, string, number, number]> = [
      [x0, y0, 'borderLeft', 'borderTop', -1, -1],
      [x1 - L, y0, 'borderRight', 'borderTop', 1, -1],
      [x0, y1 - L, 'borderLeft', 'borderBottom', -1, 1],
      [x1 - L, y1 - L, 'borderRight', 'borderBottom', 1, 1],
    ];
    const out: FxNode[] = corners.map(([x, y, b1, b2, sx, sy]) => ({
      style: {
        position: 'absolute',
        left: px(x),
        top: px(y),
        width: px(L),
        height: px(L),
        [b1]: line,
        [b2]: line,
      },
      anims: [
        {
          delay: 0,
          duration: Math.min(600, D * 0.5),
          fill: 'both',
          easing: OUT,
          keyframes: [
            { opacity: 0, transform: `translate(${px(sx * spread)}, ${px(sy * spread)})` },
            { opacity: 1, transform: 'translate(0px, 0px)' },
          ],
        },
        {
          delay: Math.min(600, D * 0.5),
          duration: Math.min(1400, D - Math.min(600, D * 0.5)),
          iterations: Math.max(1, Math.ceil((D - 600) / 1400)),
          fill: 'none',
          easing: 'ease-in-out',
          keyframes: [
            { transform: 'translate(0px, 0px)' },
            { transform: `translate(${px(sx * t * 1.5)}, ${px(sy * t * 1.5)})` },
            { transform: 'translate(0px, 0px)' },
          ],
        },
      ],
    }));
    const label = str(ctx, 'label', '');
    if (label) {
      const fs = Math.max(9, 14 * u);
      out.push({
        text: label,
        style: monoText(ctx, fs, {
          position: 'absolute',
          left: px(x0),
          top: px(Math.max(0, y0 - fs * 1.8)),
          color: c,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
        }),
        anims: [arrive(250, 400, fs * 0.5)],
      });
    }
    return out;
  },
};

export const REEL_FX_KINDS: FxKind[] = [
  hud,
  terminal,
  code,
  counter,
  bars,
  words,
  sliceText,
  textRings,
  dotGrid,
  orbit,
  ribbon,
  brackets,
];
